/**
 * Resuelve QUÉ ítem del árbol está activo. Uno solo.
 *
 * Antes cada NavItem decidía por su cuenta y resaltaba a todos los que
 * coincidían con la URL. Con tres niveles eso rompía de dos maneras:
 *
 *  - Un hermano que apunta a la raíz del grupo (p. ej. "Overview" → /coaching)
 *    coincide con TODAS las rutas del grupo (`^/coaching(/|$)`), así que se
 *    quedaba resaltado junto con la página real.
 *  - La ruta se leía de `window.location.pathname` durante el render. En routers
 *    que actualizan la URL después de renderizar (Next.js App Router lo hace en
 *    un `useInsertionEffect`), ese render veía la ruta VIEJA y dejaba resaltada
 *    la vista anterior. Por eso existe la prop `currentPath`.
 *
 * Regla: entre los ítems que coinciden gana, por orden,
 *  1. el que el consumidor marca con `active` / `isActive` (el prop manda);
 *  2. el de `href` exactamente igual a la ruta, antes que uno por `activePattern`;
 *  3. el de `href` más largo (el más específico);
 *  4. el más profundo en el árbol.
 * Los ancestros del ganador se marcan aparte (`has-active-child`).
 */

/** Ruta del router del consumidor si la pasa; si no, la del navegador. */
export function resolveCurrentPath(currentPath) {
  if (typeof currentPath === 'string') return currentPath;
  if (typeof window === 'undefined') return null;
  return window.location.pathname;
}

/**
 * Puntuación de coincidencia de un ítem, o null si no coincide.
 * Un `active` / `isActive` booleano es la respuesta: `false` descarta el ítem
 * aunque la URL coincida, igual que antes.
 */
function scoreItem(item, currentPath, level) {
  if (typeof item.active === 'boolean' || typeof item.isActive === 'boolean') {
    return item.active || item.isActive ? [3, 0, level] : null;
  }

  if (currentPath === null || !item.href) return null;

  const hrefLength = item.href.length;
  if (currentPath === item.href) return [2, hrefLength, level];
  if (item.activePattern && new RegExp(item.activePattern).test(currentPath)) {
    return [1, hrefLength, level];
  }
  return null;
}

/**
 * ¿Coincide este ítem, mirado solo? Para un NavItem usado fuera del Sidenav,
 * que no tiene árbol con el que comparar.
 */
export function matchesItem(item, currentPath) {
  return scoreItem(item, currentPath, 0) !== null;
}

function isBetter(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] > b[i];
  }
  return false;
}

/**
 * El ítem activo en `trees` (uno o varios árboles: items, footerItems), o null
 * si ninguno coincide. Devuelve el OBJETO, no su id: se compara por referencia
 * para que un árbol con ítems sin `id` no marque a todos como activos.
 */
export function resolveActiveItem(trees, currentPath) {
  let best = null;

  const walk = (items, level) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item) continue;
      const score = scoreItem(item, currentPath, level);
      if (score && (!best || isBetter(score, best.score))) {
        best = { item, score };
      }
      walk(item.children, level + 1);
    }
  };

  for (const tree of trees) walk(tree, 0);
  return best ? best.item : null;
}

/** true si `item` o alguno de sus descendientes es `activeItem`. */
export function containsActiveItem(item, activeItem) {
  if (!item || !activeItem) return false;
  if (item === activeItem) return true;
  return Array.isArray(item.children)
    ? item.children.some((child) => containsActiveItem(child, activeItem))
    : false;
}

/**
 * ¿Un clic en este ítem arranca una navegación que valga la pena señalar?
 * Solo ítems con `href` propio, internos y que no sean la página actual. Un
 * ítem que solo abre un panel o un grupo que solo se expande (sin `href`) no
 * deben quedarse "cargando". Un ítem con hijos Y `href` sí navega.
 */
export function isNavigationTarget(item, currentPath) {
  if (!item || item.disabled || item.external || !item.href) return false;
  return item.href !== currentPath;
}

/**
 * true si el consumidor marca el ítem como activo con `active` / `isActive`.
 * Con eso el consumidor dice que su router ya está en esa vista, y el Sidenav
 * lo usa para dar por terminada una navegación pendiente.
 */
export function isMarkedActive(item) {
  return !!item && (item.active === true || item.isActive === true);
}

/**
 * Busca en los árboles actuales el ítem equivalente a `target`: por referencia,
 * luego por `id` y luego por `href`. Hace falta porque muchos consumidores
 * recrean los objetos de sus ítems en cada render.
 */
export function findNavItem(trees, target) {
  if (!target) return null;
  let byId = null;
  let byHref = null;

  const walk = (items) => {
    if (!Array.isArray(items)) return null;
    for (const item of items) {
      if (!item) continue;
      if (item === target) return item;
      if (!byId && target.id !== undefined && item.id === target.id) byId = item;
      if (!byHref && target.href && item.href === target.href) byHref = item;
      const found = walk(item.children);
      if (found) return found;
    }
    return null;
  };

  for (const tree of trees) {
    const found = walk(tree);
    if (found) return found;
  }
  return byId || byHref;
}
